/* eslint-disable radix */
import React, { useState, useEffect } from 'react'
import Grid from '@material-ui/core/Grid'
import TextField from '@material-ui/core/TextField'
import { FormControl, IconButton, InputLabel, Typography, LinearProgress } from '@material-ui/core'
import { DatePicker } from '@material-ui/pickers'
import { MultiSelect } from 'react-multi-select-component'
import moment, { Moment } from 'moment'
import CalendarIcon from '@material-ui/icons/CalendarToday'

import { Dialog, UploadButton, snackbar } from 'components'
import { useCheckFormErrors, useApiCall } from 'hooks'
import { STRINGS } from 'consts'
import { CustomFormControlLabel } from 'components/CustomFormControlLabel'
import { CustomCheckbox } from 'components/CustomCheckbox'
import { PrizeCategory } from 'consts/prize'
import VisitsFilter from 'features/Main/Notifications/components/filters/VisitsFilter'
import DateRangeFilter from 'features/Main/Notifications/components/filters/DateRangeFilter'
import RewardFilter from 'features/Main/Notifications/components/filters/RewardFilter'
import { DataParams, DataResponse } from 'features/Main/Notifications/types'
import { fetchPushUserCount } from 'features/Main/Notifications/api'

import defaultIcon from '../../../../../assets/images/default-icon.png'
import { PartnerIntegration } from '../../../components/PartnerIntegration'
import { useStyles } from './styles'
import { createManualReward } from '../../api'
import { Tag } from '../../../Tags/types'
import { ManualReward, CreateManualRewardPayload, CreateManualRewardResponse } from '../../types'

interface ValidationData {
  toteatEnabled: boolean
  category?: PrizeCategory
}

const rules = {
  title: [
    {
      validate: (title: string) => title.length <= 25,
      message: 'No puede superar los 25 caracteres',
    },
  ],
  shortTitle: [
    {
      validate: (shortTitle: string) => shortTitle.length <= 22,
      message: 'No puede superar los 22 caracteres',
    },
  ],
  additionalText: [
    {
      validate: (value: string) => (value && value.length <= 100) || !value.length,
      message: 'No puede superar los 100 caracteres',
    },
  ],
  tagsSelected: [],
  icon: [],
  expiration: [],
  pushTitle: [
    {
      validate: (title?: string) => (title ? title.length <= 50 : true),
      message: 'No puede superar los 50 caracteres',
    },
  ],
  pushDescription: [
    {
      validate: (description?: string) => (description ? description.length <= 192 : true),
      message: 'No puede superar los 192 caracteres',
    },
  ],
  toteatBenefitId: [
    {
      validate: (value: string, data: ValidationData) => {
        const { toteatEnabled, category } = data
        if (toteatEnabled && category === PrizeCategory.Checkout) {
          return value.trim().length > 0
        }
        return true
      },
      message: 'El ID Beneficio Toteat es requerido',
      validateEmpty: true,
    },
  ],
  skusList: [
    {
      validate: (skusList: string[], data: ValidationData) => {
        const { toteatEnabled, category } = data
        if (toteatEnabled && category === PrizeCategory.General) {
          return skusList.length > 0
        }
        return true
      },
      message: 'El SKU es requerido',
    },
  ],
  visitsFilter: [
    {
      validate: (visitsFilter: string | null) =>
        visitsFilter == null ? true : parseInt(visitsFilter) <= 1000,
      message: 'Máximo permitido: 1000',
    },
  ],
}

interface CreateManualRewardDialogProps {
  handleClose: () => void
  onDone: (newManualReward: ManualReward) => void
  tags: Tag[]
}

const CreateManualRewardDialog: React.FC<CreateManualRewardDialogProps> = ({
  handleClose,
  onDone,
  tags,
}) => {
  const classes = useStyles()

  const [toteatBenefitId, setToteatBenefitId] = useState<string>('')
  const [currentSku, setCurrentSku] = useState<string>('')
  const [skusList, setSkusList] = useState<string[]>([])
  const [storesEnabled, setStoresEnabled] = useState(true)
  const [tucanEnabled, setTucanEnabled] = useState(false)
  const [toteatEnabled, setToteatEnabled] = useState(false)
  const [category, setCategory] = useState<PrizeCategory | undefined>()
  const [title, setTitle] = useState('')
  const [shortTitle, setShortTitle] = useState('')
  const [icon, setIcon] = useState<File | null>(null)
  const [iconPreview, setIconPreview] = useState('')
  const [image, setImage] = useState<File | null>(null)
  const [imagePreview, setImagePreview] = useState('')
  const [pushMessage, setPushMessage] = useState(false)
  const [pushTitle, setPushTitle] = useState('')
  const [pushDescription, setPushDescription] = useState('')
  const [active, setActive] = useState(false)
  // eslint-disable-next-line prettier/prettier
  const [tagsSelected, setTagsSelected] = useState<{ label: string, value: number }[]>([])
  const [expiration, setExpiration] = useState<Moment | null>(null)
  const [additionalText, setAdditionalText] = useState('')
  const firstExpirationDate = moment().format('YYYY-MM-DD hh:mm')
  const [allTagsAreRequired, setAllTagsAreRequired] = useState(false)
  const [visitsFilter, setVisitsFilter] = useState<string | null>(null)
  const [visitsOperator, setVisitsOperator] = useState<'==' | '>' | '<'>('==')
  const [startDate, setStartDate] = useState<Moment | null>(null)
  const [endDate, setEndDate] = useState<Moment | null>(null)
  const [rewardId, setRewardId] = useState<string>('')
  const [invertedLogic, setInvertedLogic] = useState<boolean>(false)
  const [userCount, setUserCount] = useState<number | null>(null)
  const [hasPartnerErrors, setHasPartnerErrors] = useState(false)

  const isEcardEnabled = process.env.REACT_APP_ECARD_ENABLED === 'true'

  const tagsForSelect = tags.map(el => ({ label: el.title, value: el.id }))

  const [createManualRewardApi, isLoading] = useApiCall<
    CreateManualRewardPayload,
    CreateManualRewardResponse
  >(createManualReward)

  // eslint-disable-next-line @typescript-eslint/member-delimiter-style
  const handleSetTags = async (e: { label: string; value: number }[]) => {
    setTagsSelected(e)
  }

  const handleAllTagsAreRequiredChange: React.ChangeEventHandler<HTMLInputElement> = async () => {
    setAllTagsAreRequired(!allTagsAreRequired)
  }

  const handleTitleChange: React.ChangeEventHandler<HTMLInputElement> = e => {
    setTitle(e.target.value)
  }

  const handleShortTitleChange: React.ChangeEventHandler<HTMLInputElement> = e => {
    setShortTitle(e.target.value)
  }

  const handleIconChange = (file: File) => {
    setIconPreview(URL.createObjectURL(file))
    setIcon(file)
  }

  const handleImageChange = (file: File) => {
    setImagePreview(URL.createObjectURL(file))
    setImage(file)
  }

  const handleChangePushMessage: React.ChangeEventHandler<HTMLInputElement> = () => {
    setPushMessage(!pushMessage)
    if (!pushMessage) {
      setPushTitle('')
      setPushDescription('')
    }
  }

  const handleChangeActive: React.ChangeEventHandler<HTMLInputElement> = () => {
    setActive(!active)
  }

  const handleChangePushTitle: React.ChangeEventHandler<HTMLInputElement> = e => {
    setPushTitle(e.target.value)
  }

  const handleChangepushDescription: React.ChangeEventHandler<HTMLInputElement> = e => {
    setPushDescription(e.target.value)
  }

  const handleDateChange = (newDate: Moment | null) => {
    setExpiration(newDate)
  }

  const handleAdditionalTextChange: React.ChangeEventHandler<HTMLInputElement> = e => {
    setAdditionalText(e.target.value)
  }

  const getRequiredFields = () => {
    const baseRequiredFields = ['title', 'shortTitle', 'additionalText', 'expiration', 'icon']
    const pushTitleRequiredFields = ['pushTitle', 'pushDescription']

    if (pushMessage) {
      baseRequiredFields.push(...pushTitleRequiredFields)
    }

    if (toteatEnabled) {
      if (category === PrizeCategory.General) {
        baseRequiredFields.push('skusList')
      } else if (category === PrizeCategory.Checkout) {
        baseRequiredFields.push('toteatBenefitId')
      }
    }

    return baseRequiredFields
  }

  const hasPushNotificaitonAndWasntCompleted =
    pushMessage && (!image || !pushDescription || !pushTitle)

  const fields = {
    title,
    shortTitle,
    icon,
    tagsSelected,
    additionalText,
    expiration,
    pushMessage,
    pushTitle,
    pushDescription,
    toteatEnabled,
    toteatBenefitId,
    skusList,
    category,
    visitsFilter,
  }
  const { hasErrors, isAnyFieldEmpty, errors } = useCheckFormErrors(
    fields,
    rules,
    getRequiredFields(),
  )
  const isSubmitDisabled =
    hasErrors ||
    isAnyFieldEmpty ||
    isLoading ||
    hasPushNotificaitonAndWasntCompleted ||
    hasPartnerErrors

  const onCreateManualReward = async () => {
    if (!icon || !expiration) return
    if (pushMessage && (!image || !pushDescription || !pushTitle)) {
      snackbar.show('Todos los campos de la push son requeridos.')
      return
    }
    //! tagsSelected.length
    const manualReward = {
      title,
      shortTitle,
      icon,
      image,
      pushMessage,
      pushTitle,
      pushDescription,
      active,
      tagsSelected,
      expiration: expiration.toISOString(),
      additionalText,
      allTagsAreRequired,
      ...(visitsFilter !== null ? { visitsValue: visitsFilter } : {}),
      visitsOperator,
      ...(startDate?.toISOString() ? { dateFrom: startDate?.toISOString() } : {}),
      ...(endDate?.toISOString() ? { dateTo: endDate?.toISOString() } : {}),
      ...(rewardId ? { rewardId } : {}),
      rewardInverted: invertedLogic,
      category,
      storesEnabled,
      tucanEnabled,
      toteatEnabled,
      skus: skusList,
      toteatBenefitId,
    }
    try {
      const { manualReward: newManualReward } = await createManualRewardApi(manualReward)
      onDone(newManualReward)
    } catch (err) {
      console.error(err)
      snackbar.show('No se pudo crear premio. Intente de nuevo.')
    }
  }

  const [requestData, isRequestingData] = useApiCall<DataParams, DataResponse>(fetchPushUserCount)

  const handleFetchPushUserCount = async () => {
    try {
      const tagsSelectedId = tagsSelected.map((tag: { value: number }) => tag.value)

      const data = await requestData({
        visitsValue:
          visitsFilter !== null && visitsFilter.length ? parseInt(visitsFilter) : undefined,
        visitsOperator,
        dateFrom: startDate?.toISOString() || undefined,
        dateTo: endDate?.toISOString() || undefined,
        rewardId: rewardId ? parseInt(rewardId) : undefined,
        rewardInverted: invertedLogic,
        tagIds: tagsSelectedId,
        allTagsAreRequired,
      })

      setUserCount(data.count)
    } catch (err) {
      console.error(err)
      snackbar.show('No se pudo calcular la cantidad de usuarios. Intente de nuevo.')
    }
  }

  useEffect(() => {
    handleFetchPushUserCount()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    visitsFilter,
    visitsOperator,
    startDate,
    endDate,
    rewardId,
    invertedLogic,
    tagsSelected,
    allTagsAreRequired,
  ])

  useEffect(() => {
    if (isEcardEnabled) {
      const file = new File([defaultIcon], 'default-icon.png', { type: 'image/png' })
      handleIconChange(file)
      handleImageChange(file)
    }
  }, [isEcardEnabled])

  const handlePartnerValidationChange = (hasErrorsPartner: boolean) => {
    setHasPartnerErrors(hasErrorsPartner)
  }

  return (
    <Dialog
      title="Nuevo reward manual"
      isOpen
      showActions
      onCancel={handleClose}
      isLoading={isLoading}
      okButtonText="Crear"
      okButtonProps={{ disabled: isSubmitDisabled }}
      onAccept={onCreateManualReward}
    >
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Typography variant="h6">Descripción del reward</Typography>
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="Título"
            value={title}
            fullWidth
            onChange={handleTitleChange}
            disabled={isLoading}
            required
            error={errors.title.hasError}
            helperText={errors.title.message}
            inputProps={{ maxLength: 25 }}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="Descripcion"
            value={shortTitle}
            fullWidth
            onChange={handleShortTitleChange}
            disabled={isLoading}
            required
            error={errors.shortTitle.hasError}
            helperText={errors.shortTitle.message}
            inputProps={{ maxLength: 60 }}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="Texto adicional"
            value={additionalText}
            fullWidth
            onChange={handleAdditionalTextChange}
            disabled={isLoading}
            required
            error={errors.additionalText.hasError}
            helperText={errors.additionalText.message}
          />
        </Grid>
        <Grid item xs={12}>
          <FormControl className={classes.fullWidth}>
            <InputLabel shrink className={classes.multiselectTitle}>
              Tag
            </InputLabel>
            <MultiSelect
              options={tagsForSelect}
              value={tagsSelected}
              onChange={handleSetTags}
              ClearIcon
              labelledBy="Seleccionar Tags"
              hasSelectAll={false}
              className={classes.outlinedMultiSelect}
            />
          </FormControl>
        </Grid>
        <Grid item xs={12}>
          <CustomFormControlLabel
            control={
              <CustomCheckbox
                onChange={handleAllTagsAreRequiredChange}
                checked={allTagsAreRequired}
              />
            }
            label="Sólo usuarios con todos los tags seleccionados"
          />
        </Grid>
        <Grid item xs={12}>
          <Typography variant="subtitle1" className={classes.dialogSubTitle}>
            Rango de visitantes
          </Typography>
          <Grid item xs={12}>
            <VisitsFilter
              visitsFilter={visitsFilter}
              setVisitsFilter={setVisitsFilter}
              visitsOperator={visitsOperator}
              setVisitsOperator={setVisitsOperator}
              error={errors.visitsFilter.hasError}
              helperText={errors.visitsFilter.message}
            />
          </Grid>
          <Grid item xs={12}>
            <DateRangeFilter
              startDate={startDate}
              setStartDate={setStartDate}
              endDate={endDate}
              setEndDate={setEndDate}
            />
          </Grid>
        </Grid>

        <Grid item xs={12}>
          <Typography variant="subtitle1" className={classes.dialogSubTitle}>
            Premio
          </Typography>
          <RewardFilter
            rewardId={rewardId}
            setRewardId={setRewardId}
            invertedLogic={invertedLogic}
            setInvertedLogic={setInvertedLogic}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <DatePicker
            value={expiration}
            onChange={handleDateChange}
            autoOk
            disablePast
            format={STRINGS.DATE_FORMAT}
            minDate={firstExpirationDate}
            initialFocusedDate={firstExpirationDate}
            disabled={isLoading}
            style={{ width: '100%' }}
            minDateMessage="La fecha de vencimiento no puede ser anterior a hoy"
            label="Vencimiento"
            required
            InputProps={{
              endAdornment: (
                <IconButton>
                  <CalendarIcon color="primary" />
                </IconButton>
              ),
            }}
          />
        </Grid>
        <Grid container item xs={12}>
          <Grid item xs={6}>
            <CustomFormControlLabel
              control={<CustomCheckbox onChange={handleChangePushMessage} checked={pushMessage} />}
              label="Sale con push?"
            />
          </Grid>
          <Grid item xs={6}>
            <CustomFormControlLabel
              control={<CustomCheckbox onChange={handleChangeActive} checked={active} />}
              label="Activar Reward"
            />
          </Grid>
        </Grid>
        {pushMessage && (
          <>
            <Grid item xs={12}>
              <TextField
                label="Titulo push"
                value={pushTitle}
                fullWidth
                required
                error={errors.pushTitle.hasError}
                helperText={errors.pushTitle.message}
                onChange={handleChangePushTitle}
                disabled={isLoading}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Descripción push"
                value={pushDescription}
                fullWidth
                required
                error={errors.pushDescription.hasError}
                helperText={errors.pushDescription.message}
                onChange={handleChangepushDescription}
                disabled={isLoading}
              />
            </Grid>
            {!isEcardEnabled && (
              <Grid item xs={12} sm={12} style={{ marginTop: 24 }}>
                <div className={classes.iconUploadContainer}>
                  <UploadButton
                    id="manualReward-image-upload-button"
                    accept=".jpg, .jpeg, .png"
                    label="Subir imagen popup"
                    onChange={handleImageChange}
                    maxSizeInMB={2}
                  />
                  <div className={classes.iconUploadPreviewContainer}>
                    <img src={imagePreview} alt="" className={classes.iconPreview} />
                  </div>
                </div>
              </Grid>
            )}
          </>
        )}
        {!isEcardEnabled && (
          <Grid item xs={12} sm={12} style={{ marginBottom: 24 }}>
            <div className={classes.iconUploadContainer}>
              <UploadButton
                id="manualReward-icon-upload-button"
                accept=".jpg, .jpeg, .png"
                label="Subir icono"
                onChange={handleIconChange}
                maxSizeInMB={2}
              />
              <div className={classes.iconUploadPreviewContainer}>
                <img src={iconPreview} alt="" className={classes.iconPreview} />
              </div>
            </div>
          </Grid>
        )}
        <PartnerIntegration
          storesEnabled={storesEnabled}
          setStoresEnabled={setStoresEnabled}
          tucanEnabled={tucanEnabled}
          setTucanEnabled={setTucanEnabled}
          toteatEnabled={toteatEnabled}
          setToteatEnabled={setToteatEnabled}
          category={category}
          setCategory={setCategory}
          currentSku={currentSku}
          setCurrentSku={setCurrentSku}
          skusList={skusList}
          setSkusList={setSkusList}
          toteatBenefitId={toteatBenefitId}
          setToteatBenefitId={setToteatBenefitId}
          errors={errors}
          isLoading={isLoading}
          onValidationChange={handlePartnerValidationChange}
        />
        <Grid item xs={12}>
          <InputLabel>
            {isRequestingData ? (
              <LinearProgress style={{ marginTop: 25, marginBottom: 25 }} />
            ) : (
              <div className={classes.tootlTipUsers}>
                Esta acción impactará a {userCount} usuarios
              </div>
            )}
          </InputLabel>
        </Grid>
      </Grid>
    </Dialog>
  )
}

export { CreateManualRewardDialog }
